/**
 * Activity filter model.
 * Selection stores: Show all | activity-scope UUIDs | sku UUIDs
 * | occurrence keys (parent>child) so a shared child is independent per parent.
 * Activity scopes nest other activity scopes (via links / groupTypes) and SKUs.
 * Scopes tab: same tree as SKU tab without VO/BO wrappers or SKU rows.
 * SKU tab: highest parent scope at top; VO / BO are the last children before SKUs.
 */

import { formatRecordLogLabel, recordStaffCodeFromFields } from 'pages/Records/recordLogLabel';
import {
  resolveAssetRentingLinkedComponentRecords,
  resolveProductSkuIds,
} from 'pages/Records/operationsCatalog';

export type ActivityGroupKind =
  | 'organization'
  | 'property'
  | 'building'
  | 'level'
  | 'department'
  | 'sku'
  | 'custom';

/** Object-group wrappers on the SKU tab. Stored id stays Actions. */
export type ActivitySkuType = 'VO' | 'Actions';

export const SKU_TOP_VO_ID: ActivitySkuType = 'VO';
export const SKU_TOP_ACTIONS_ID: ActivitySkuType = 'Actions';

/** Department partition headers — fixed labels under each department scope. */
export const DEPARTMENT_VO_HEADER = 'Value Objects (VO)';
export const DEPARTMENT_BO_HEADER = 'Business Objects (BO)';

/** SKU-tab BO leaf ids — stable slug from log Action column text. */
export const BO_ACTION_LEAF_PREFIX = 'bo_act__';

/** SKU-tab object-group row: e.g. Egg fried rice(VO). */
export const skuObjectGroupLabel = (scopeName: string, skuType: ActivitySkuType): string => (
  `${String(scopeName || '').trim() || (skuType === 'VO' ? 'Value Objects' : 'Business Objects')}(${
    skuType === 'VO' ? 'VO' : 'BO'
  })`
);

export const boActionLeafId = (actionLabel: string): string => {
  const label = String(actionLabel || '').trim();
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48);
  return `${BO_ACTION_LEAF_PREFIX}${slug || 'unnamed'}`;
};

export const isBoActionLeafId = (value: string): boolean => (
  String(value || '').trim().startsWith(BO_ACTION_LEAF_PREFIX)
);
/** Activity scope row that can list SKUs (and optionally nest further scopes via children). */
export type ActivityGroupType = {
  id: string,
  name: string,
  skuIds: string[],
};

/** Activity scope — expandable row; may nest child scopes (links) and leaf scopes (groupTypes). */
export type ActivityGroup = {
  id: string,
  name: string,
  kind: ActivityGroupKind,
  groupTypes: ActivityGroupType[],
};

/** Parent → immediate child activity scope. Tree walks one level at a time. */
export type ActivityGroupLink = {
  parentId: string,
  childId: string,
};

export type ActivitySkuRef = {
  id: string,
  name: string,
  /** True when inventory price or any log line value is non-zero. */
  hasValue?: boolean,
};

export type ActivityLogSkuRef = {
  id?: string | null,
  name?: string | null,
  value?: string | number | null,
};

/**
 * Shared Scopes / SKU tree node. Immediate children only — never flatten grandchildren.
 * Object-group nodes (VO/BO) are last children before SKUs on the SKU tab.
 */
export type ActivitySkuHierarchyGroup = {
  id: string,
  name: string,
  skus: ActivitySkuRef[],
  skuIds: string[],
  /** Self (if this node is a leaf activity scope) plus descendant activity-scope ids. */
  groupTypeIds: string[],
  children: ActivitySkuHierarchyGroup[],
  /** VO / BO wrapper — hidden on Scopes tab except department partitions. */
  isObjectGroup?: boolean,
  /** Groups-tab VO master-data leaves (activity_logs.action_data_id). */
  groupsVoRefs?: ActivitySkuRef[],
  /** Groups-tab BO leaves — one checkbox per unique event_type. */
  boActionRefs?: ActivitySkuRef[],
  /** Department partition VO header under a department scope. */
  isDepartmentPartitionGroup?: boolean,
  /** Department partition BO header — expandable list of event_type rows. */
  isDepartmentBoAggregate?: boolean,
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const SHOW_ALL_ACTIVITY_LOGS = 'Show all';

/** Static /logs scope row — parent of every possible Organization record. */
export const ALL_ACTIVITY_SCOPE_ID = '__all_activity__';
export const ALL_ACTIVITY_SCOPE_NAME = 'Organization';

export const ORGANIZATION_ACTIVITY_SCOPE_PREFIX = '__org__';

export type ActivityOrganizationRef = {
  id: string,
  name: string,
  /** erp_records Organization record id when one exists. */
  recordId?: string,
};

export const organizationActivityScopeId = (organizationId: string): string => (
  `${ORGANIZATION_ACTIVITY_SCOPE_PREFIX}${String(organizationId || '')}`
);

export const isOrganizationActivityScopeId = (value: string): boolean => (
  String(value || '').startsWith(ORGANIZATION_ACTIVITY_SCOPE_PREFIX)
);

/** Real activity_scopes row with kind organization (Blue Primus, etc.). */
export const isOrganizationKindScopeId = (
  scopeId: string,
  model?: ActivityGroupsModel | null,
): boolean => model?.byId.get(String(scopeId || '').trim())?.kind === 'organization';

/** Structural org row in /logs — legacy __org__ token or kind=organization scope. */
export const isStructuralOrganizationScopeId = (
  scopeId: string,
  model?: ActivityGroupsModel | null,
): boolean => (
  isOrganizationActivityScopeId(scopeId) || isOrganizationKindScopeId(scopeId, model)
);

/** Map saved __org__{organizationId} picks to the real organization scope id. */
export const migrateLegacyOrganizationChecked = (
  checked: string[],
  organizationId: string,
  model?: ActivityGroupsModel | null,
): string[] => {
  const orgScope = (model?.groups || []).find((g) => g.kind === 'organization');
  if (!orgScope?.id || !organizationId) return checked || [];
  const legacyId = organizationActivityScopeId(organizationId);
  return (checked || []).map((token) => (
    String(token || '').trim() === legacyId ? orgScope.id : token
  ));
};

/** Metrics Departments — all department-kind scopes; filter uses a prebuilt index. */
export const DEPARTMENTS_METRIC_SCOPE_ID = '__departments_metric__';

/** Root department scope — not a branch filter. */
export const DEPARTMENTS_ROOT_SCOPE_NAME = 'Departments';

/** Nested tree checkbox: parentId>childId (UUIDs never contain '>'). */
export const ACTIVITY_OCCURRENCE_SEP = '>';

export const isActivityGroupUuid = (value: string): boolean => UUID_RE.test(String(value || '').trim());

/**
 * Inventory / SKU selection ids (e.g. inv_001). Not legacy type labels like "Direct".
 * Opaque tokens: no spaces, and a digit or underscore.
 */
export const isActivitySkuSelectionId = (value: string): boolean => {
  const c = String(value || '').trim();
  if (!c || c === SHOW_ALL_ACTIVITY_LOGS || /\s/.test(c)) return false;
  if (c === ALL_ACTIVITY_SCOPE_ID || isOrganizationActivityScopeId(c)) return false;
  if (isActivityGroupUuid(c)) return true;
  if (isBoActionLeafId(c)) return true;
  return /[0-9_]/.test(c);
};

/** SKU-tab wrapper id: VO__scopeId or Actions__scopeId. */
export const isActivityObjectGroupWrapperId = (value: string): boolean => {
  const c = String(value || '').trim();
  return c.startsWith(`${SKU_TOP_VO_ID}__`) || c.startsWith(`${SKU_TOP_ACTIONS_ID}__`);
};

/** True when every path segment is a scope UUID, SKU id, or VO/BO wrapper id. */
export const isActivityOccurrenceKey = (value: string): boolean => {
  const parts = String(value || '').split(ACTIVITY_OCCURRENCE_SEP);
  if (parts.length < 2) return false;
  return parts.every(
    (p) => isActivityGroupUuid(p)
      || isActivitySkuSelectionId(p)
      || isActivityObjectGroupWrapperId(p)
      || isBoActionLeafId(p),
  );
};

export const activityOccurrenceKey = (parentPath: string[], nodeId: string): string => (
  parentPath.length
    ? `${parentPath.join(ACTIVITY_OCCURRENCE_SEP)}${ACTIVITY_OCCURRENCE_SEP}${nodeId}`
    : nodeId
);

export const selectionIdFromCheckedToken = (token: string): string => {
  const c = String(token || '').trim();
  const i = c.lastIndexOf(ACTIVITY_OCCURRENCE_SEP);
  return i === -1 ? c : c.slice(i + 1);
};

export const parseActivityOccurrenceKey = (
  key: string,
): { path: string[], leaf: string } | null => {
  if (!isActivityOccurrenceKey(key)) return null;
  const parts = String(key).split(ACTIVITY_OCCURRENCE_SEP);
  return { path: parts.slice(0, -1), leaf: parts[parts.length - 1] };
};

/** Branch department names encoded in an occurrence path (Bar, Restaurant, …). */
export const branchDepartmentNamesInPath = (
  pathIds: string[],
  model: ActivityGroupsModel,
): string[] => {
  const names: string[] = [];
  (pathIds || []).forEach((id) => {
    const group = model.byId.get(id);
    if (!group || group.kind !== 'department') return;
    const name = String(group.name || '').trim();
    if (!name || name.toLowerCase() === DEPARTMENTS_ROOT_SCOPE_NAME.toLowerCase()) return;
    names.push(name);
  });
  return names;
};

export const logDepartmentName = (log: {
  department?: string | null,
  departments?: string | null,
}): string => String(log.departments || log.department || '').trim();

/** When a branch is in the path, log.departments must match it. */
export const logMatchesBranchDepartments = (
  logDept: string,
  branchNames: string[],
): boolean => {
  if (!branchNames.length) return true;
  if (!logDept) return false;
  const d = logDept.toLowerCase();
  return branchNames.some((n) => n.toLowerCase() === d);
};

/** Search / log match uses real ids — last segment of each occurrence key. */
export const flattenCheckedToSelectionIds = (
  checked: string[],
  model?: ActivityGroupsModel | null,
): string[] => {
  if (checkedIncludesShowAll(checked)) return [SHOW_ALL_ACTIVITY_LOGS];
  return uniqueIds(
    (checked || [])
      .map(selectionIdFromCheckedToken)
      .filter((id) => (
        id
        && id !== SHOW_ALL_ACTIVITY_LOGS
        && id !== ALL_ACTIVITY_SCOPE_ID
        && !isStructuralOrganizationScopeId(id, model)
      )),
  );
};

/** Persist Show all | activity-scope UUIDs | SKU ids | parent>child keys. */
export const migrateActivityGroupsChecked = (checked: string[]): string[] => {
  const next: string[] = [];
  const seen = new Set<string>();
  (checked || []).forEach((raw) => {
    const c = String(raw || '').trim();
    if (!c || seen.has(c)) return;
    if (
      c === SHOW_ALL_ACTIVITY_LOGS
      || c === ALL_ACTIVITY_SCOPE_ID
      || c === DEPARTMENTS_METRIC_SCOPE_ID
      || isOrganizationActivityScopeId(c)
      || isActivityGroupUuid(c)
      || isActivitySkuSelectionId(c)
      || isActivityOccurrenceKey(c)
      || isBoActionLeafId(c)
    ) {
      seen.add(c);
      next.push(c);
    }
  });
  return next;
};

export type ActivityGroupsModel = {
  groups: ActivityGroup[],
  byId: Map<string, ActivityGroup>,
  groupTypeById: Map<string, ActivityGroupType>,
  /** Lowercased activity-scope name → id (first wins). */
  groupTypeNameToId: Map<string, string>,
  /** Lowercased activity-scope row name → scope id (tracker + DB scopes). */
  scopeNameToId: Map<string, string>,
  groupTypeIds: string[],
  groupTypeIdSet: Set<string>,
  /** Parent scope id → immediate child scope ids. */
  childrenByParentId: Map<string, string[]>,
  /** Flat unique SKUs (logs preferred, then groupTypes.skuIds + inventory names). */
  skus: ActivitySkuRef[],
  skuIds: string[],
  skuIdSet: Set<string>,
  /** Shared tree: root activity scopes → nested scopes → VO/BO → SKUs. */
  skuHierarchy: ActivitySkuHierarchyGroup[],
  /** Scope / linked-sku bucket id → inventory ids claimed by that leaf (and descendants). */
  skuIdsByLeafId: Map<string, Set<string>>,
  /** Master-data ids (action_data_id) shown under department VO on Groups tab. */
  masterDataIdSet: Set<string>,
  /** SKU-tab BO action leaf ids (bo_act__…). */
  boActionLeafIdSet: Set<string>,
  /** Department scope ids that own a VO/BO log partition. */
  departmentPartitionScopeIds: Set<string>,
  /** activity_logs.head_scope erp_records id → department activity-scope id. */
  headScopeToDeptScopeId: Map<string, string>,
  /** Master-data id → department scope that owns it in the Groups VO partition. */
  masterDataDeptScopeId: Map<string, string>,
  /** SKU-tab BO leaf id → department scope that owns it. */
  boActionDeptScopeId: Map<string, string>,
};

const emptyModel = (): ActivityGroupsModel => ({
  groups: [],
  byId: new Map(),
  groupTypeById: new Map(),
  groupTypeNameToId: new Map(),
  scopeNameToId: new Map(),
  groupTypeIds: [],
  groupTypeIdSet: new Set(),
  childrenByParentId: new Map(),
  skus: [],
  skuIds: [],
  skuIdSet: new Set(),
  skuHierarchy: [],
  skuIdsByLeafId: new Map(),
  masterDataIdSet: new Set(),
  boActionLeafIdSet: new Set(),
  departmentPartitionScopeIds: new Set(),
  headScopeToDeptScopeId: new Map(),
  masterDataDeptScopeId: new Map(),
  boActionDeptScopeId: new Map(),
});

const normalizeKind = (kind: unknown): ActivityGroupKind => {
  const k = String(kind || '').trim().toLowerCase();
  if (
    k === 'organization' || k === 'property' || k === 'building' || k === 'level'
    || k === 'department' || k === 'sku' || k === 'custom'
  ) {
    return k;
  }
  return 'custom';
};

/** Non-empty / non-zero cash or metric value on a SKU. */
export const skuValueIsPresent = (value: unknown): boolean => {
  if (value == null || value === '') return false;
  const n = Number(value);
  if (Number.isFinite(n)) return n !== 0;
  return String(value).trim().length > 0;
};

const normalizeGroupTypes = (raw: unknown): ActivityGroupType[] => {
  if (!Array.isArray(raw)) return [];
  const out: ActivityGroupType[] = [];
  const seen = new Set<string>();
  raw.forEach((gt) => {
    const id = String(gt?.id || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    const skuRaw = gt?.skuIds ?? gt?.sku_ids;
    const skuIds = Array.isArray(skuRaw)
      ? skuRaw.map((s: unknown) => String(s || '').trim()).filter(Boolean)
      : [];
    out.push({
      id,
      name: String(gt?.name || '').trim() || id,
      skuIds,
    });
  });
  return out;
};

/** Unique SKUs from loaded logs' log.skus[] (id + name + hasValue). */
export const uniqueSkusFromLogs = (
  logs: Array<{ skus?: ActivityLogSkuRef[] | null }> | null | undefined,
): ActivitySkuRef[] => {
  const byId = new Map<string, { name: string, hasValue: boolean }>();
  (logs || []).forEach((log) => {
    (log.skus || []).forEach((s) => {
      const id = String(s?.id || '').trim();
      if (!id) return;
      const name = String(s?.name || '').trim();
      const hasValue = skuValueIsPresent(s?.value);
      const prev = byId.get(id);
      if (!prev) {
        byId.set(id, { name: name || id, hasValue });
        return;
      }
      // Prefer a real name over id placeholder; OR hasValue across lines.
      byId.set(id, {
        name: (name && prev.name === id) ? name : prev.name,
        hasValue: prev.hasValue || hasValue,
      });
    });
  });
  return Array.from(byId.entries())
    .map(([id, row]) => ({ id, name: row.name, hasValue: row.hasValue }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export type BuildActivityGroupsModelOptions = {
  /** Parent → child activity-scope edges for the shared tree. */
  links?: ActivityGroupLink[] | null,
  logs?: Array<{
    skus?: ActivityLogSkuRef[] | null,
    action_data_id?: string | null,
    actionDataId?: string | null,
    action?: string | null,
    type?: string | null,
    eventType?: string | null,
    event_type?: string | null,
    department?: string | null,
    departments?: string | null,
    head_scope?: string | null,
    headScope?: string | null,
    tracking_items?: Array<{
      trackingItem?: string,
      recordKind?: string,
      recordCode?: string,
      value?: string,
    }> | null,
    trackingItems?: Array<{
      trackingItem?: string,
      recordKind?: string,
      recordCode?: string,
      value?: string,
    }> | null,
  }> | null,
  /** Server-loaded logs for department VO/BO partitions (full set, not search page). */
  partitionLogs?: BuildActivityGroupsModelOptions['logs'],
  inventory?: Array<{
    id?: string | null,
    name?: string | null,
    price?: string | number | null,
  }> | null,
  /** erp_records rows — map activity_logs.head_scope ids to department scopes. */
  erpRecords?: Array<{
    id?: string | null,
    kind?: string | null,
    name?: string | null,
    parent_id?: string | null,
    parentId?: string | null,
    fields?: Record<string, string> | null,
  }> | null,
  /**
   * Unfiltered org/location/department/management rows from getactivitygroups —
   * getrecords may hide structural rows and break head_scope → department mapping.
   */
  headScopeErpRecords?: BuildActivityGroupsModelOptions['erpRecords'],
};

type DepartmentLogPartition = {
  masterData: ActivitySkuRef[],
  skuVoItems: ActivitySkuRef[],
  boActions: ActivitySkuRef[],
};

/** Real department branch scope — not the synthetic Departments root row. */
export const isDepartmentPartitionScope = (
  scopeId: string,
  model?: ActivityGroupsModel | null,
): boolean => {
  const group = model?.byId.get(String(scopeId || '').trim());
  if (!group || group.kind !== 'department') return false;
  return group.name.toLowerCase() !== DEPARTMENTS_ROOT_SCOPE_NAME.toLowerCase();
};

const logActionDataId = (log: {
  action_data_id?: string | null,
  actionDataId?: string | null,
}): string => String(log.action_data_id || log.actionDataId || '').trim();

const logActionColumnLabel = (log: {
  type?: string | null,
  action?: string | null,
}): string => String(log.type || log.action || '').trim();

const RECORD_SAVE_EVENT_TYPES = new Set([
  'Record added',
  'Record change',
  'Record updated',
  'Record removed',
]);

/** Meta / stay rows — never BO partition leaves (audit log types stay in Actions). */
const BO_PARTITION_EXCLUDED_EVENT_TYPES = new Set([
  'Housekeeping',
]);

const logEventType = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
): string => String(
  (log as { eventType?: string | null, event_type?: string | null }).eventType
  || (log as { event_type?: string | null }).event_type
  || '',
).trim();

/** Parse activity_logs.value when this row is a money transaction. */
const logTransactionPrice = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
): number | null => {
  const eventType = logEventType(log);
  if (RECORD_SAVE_EVENT_TYPES.has(eventType)) return null;
  const raw = (log as { value?: string | number | null }).value;
  if (raw == null || raw === '' || raw === 'N/A') return null;
  const num = Number(String(raw).replace(/,/g, '').trim());
  return Number.isFinite(num) ? num : null;
};

/** True when activity_logs.value holds a non-zero transaction amount. */
const logHasMoneyTransactionValue = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
): boolean => {
  const price = logTransactionPrice(log);
  return price != null && price !== 0;
};

/**
 * Groups-tab VO — real money logs only (non-zero value, not record-save event types).
 */
const logIsVoMoneyLog = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
): boolean => {
  const price = logTransactionPrice(log);
  if (price == null || price === 0) return false;
  const eventType = logEventType(log);
  if (RECORD_SAVE_EVENT_TYPES.has(eventType)) return false;
  return true;
};

/** VO checkbox id — action_data_id when set, else stable fallback from trackers / action. */
const resolveVoCheckboxId = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
): string => {
  const actionDataId = logActionDataId(log);
  if (actionDataId) return actionDataId;
  const actionLabel = logActionColumnLabel(log);
  return actionLabel ? `voaction__${boActionLeafId(actionLabel)}` : '';
};

/** VO checkbox label — master-data Name(CODE) from action_data_id, else log action field. */
const resolveVoCheckboxLabel = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
  voId: string,
  erpById: Map<string, ErpRecordPartitionRow>,
): string => {
  const actionDataId = logActionDataId(log);
  if (actionDataId && erpById.has(actionDataId)) {
    return masterDataVoLabel(actionDataId, erpById);
  }
  // Prefer tracking Name/CODE when erp row is not loaded yet — never show bare UUIDs.
  if (actionDataId) {
    const fromTrackers = voLabelFromLogTrackers(log, actionDataId);
    if (fromTrackers) return fromTrackers;
  }
  if (voId.startsWith('txcode__')) {
    const code = voId.slice('txcode__'.length);
    const byCode = [...erpById.values()].find(
      (row) => recordStaffCodeFromFields(row.kind, row.fields) === code
        || row.id === code,
    );
    if (byCode) {
      return formatRecordLogLabel(
        byCode.name,
        recordStaffCodeFromFields(byCode.kind, byCode.fields),
        byCode.id,
      );
    }
    return code;
  }
  const actionLabel = logActionColumnLabel(log);
  return actionLabel || voId;
};

/** Prefer Name(CODE) from tracking_items matching this VO id. */
const voLabelFromLogTrackers = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
  actionDataId: string,
): string => {
  const items = trackingItemsOnLog(log);
  for (const item of items) {
    const codeOrId = String(item?.recordCode || '').trim();
    if (!codeOrId || codeOrId !== actionDataId) continue;
    const name = String((item as { recordName?: string })?.recordName || item?.value || '').trim();
    const code = String((item as { recordStaffCode?: string })?.recordStaffCode || '').trim();
    if (name || code) return formatRecordLogLabel(name, code, actionDataId);
  }
  return '';
};

/**
 * BO partition label — prefer activity_logs.event_type; fall back like logRowToDoc
 * (eventType = event_type ?? action) so Staff logged in and legacy rows match filters.
 */
const logBoPartitionEventType = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
): string => {
  const fromColumn = String((log as { event_type?: string | null }).event_type ?? '').trim();
  if (fromColumn) return fromColumn;
  const fromDoc = String((log as { eventType?: string | null }).eventType ?? '').trim();
  if (fromDoc) return fromDoc;
  // Pre-split rows — action/type is the log kind when there is no master-data id.
  if (!logActionDataId(log)) return logActionColumnLabel(log);
  return '';
};

/** Groups-tab BO checkbox label — unique event_type per department. */
const logBoActionLabel = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
): string => logBoPartitionEventType(log);

type ErpRecordPartitionRow = {
  id: string,
  kind: string,
  name: string,
  parentId: string,
  fields: Record<string, string>,
};

const mergeErpRecordsForPartitions = (
  headScopeErpRecords: BuildActivityGroupsModelOptions['erpRecords'],
  erpRecords: BuildActivityGroupsModelOptions['erpRecords'],
): ErpRecordPartitionRow[] => {
  const byId = new Map<string, ErpRecordPartitionRow>();
  const ingest = (raw: BuildActivityGroupsModelOptions['erpRecords']) => {
    (raw || []).forEach((row) => {
      const id = String(row?.id || '').trim();
      if (!id || byId.has(id)) return;
      byId.set(id, {
        id,
        kind: String(row?.kind || '').trim().toLowerCase(),
        name: String(row?.name || '').trim(),
        parentId: String(row?.parent_id || row?.parentId || '').trim(),
        fields: (row?.fields && typeof row.fields === 'object') ? row.fields : {},
      });
    });
  };
  ingest(erpRecords);
  ingest(headScopeErpRecords);
  return Array.from(byId.values());
};

const masterDataVoLabel = (
  actionDataId: string,
  erpById: Map<string, ErpRecordPartitionRow>,
): string => {
  const row = erpById.get(actionDataId);
  if (!row) return actionDataId;
  return formatRecordLogLabel(
    row.name,
    recordStaffCodeFromFields(row.kind, row.fields),
    actionDataId,
  );
};

const collectSkuVoRefsFromLog = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
  skuVoById: Map<string, ActivitySkuRef>,
) => {
  trackingItemsOnLog(log).forEach((item) => {
    if (String(item?.trackingItem || '') !== 'track_sku') return;
    const kind = String(item?.recordKind || '').trim().toLowerCase();
    if (kind !== 'sku' && kind !== 'asset_component') return;
    const id = String(item?.recordCode || '').trim();
    if (!id) return;
    const label = String(item?.value || '').trim() || id;
    skuVoById.set(id, { id, name: label });
  });
  (log.skus || []).forEach((s) => {
    const id = String(s?.id || '').trim();
    if (!id) return;
    const skuName = String(s?.name || '').trim() || id;
    skuVoById.set(id, {
      id,
      name: skuName,
      hasValue: skuValueIsPresent(s?.value),
    });
  });
};

const enrichPartitionSkuVoFromMasterData = (
  part: {
    masterDataById: Map<string, ActivitySkuRef>,
    skuVoById: Map<string, ActivitySkuRef>,
  },
  erpRecords: ErpRecordPartitionRow[],
) => {
  const recordLabel = (row: ErpRecordPartitionRow): string => (
    formatRecordLogLabel(
      row.name,
      recordStaffCodeFromFields(row.kind, row.fields),
      row.id,
    )
  );
  const addSkuOrComponent = (row: ErpRecordPartitionRow | undefined) => {
    if (!row?.id) return;
    if (row.kind !== 'sku' && row.kind !== 'asset_component') return;
    part.skuVoById.set(row.id, { id: row.id, name: recordLabel(row) });
  };

  part.masterDataById.forEach((_vo, masterDataId) => {
    const master = erpRecords.find((row) => row.id === masterDataId);
    if (!master) return;
    if (master.kind === 'sku' || master.kind === 'asset_component') {
      addSkuOrComponent(master);
      return;
    }
    if (master.kind === 'product') {
      resolveProductSkuIds(master.fields).forEach((skuId) => {
        addSkuOrComponent(erpRecords.find((row) => row.id === skuId));
      });
      return;
    }
    if (master.kind === 'asset') {
      resolveAssetRentingLinkedComponentRecords(master, erpRecords).forEach(addSkuOrComponent);
    }
  });
};

const trackingItemsOnLog = (
  log: BuildActivityGroupsModelOptions['logs'] extends (infer T)[] | null | undefined ? T : never,
): Array<{ trackingItem?: string, recordKind?: string, recordCode?: string, value?: string }> => {
  const raw = (log as { tracking_items?: unknown, trackingItems?: unknown })?.tracking_items
    ?? (log as { trackingItems?: unknown })?.trackingItems;
  return Array.isArray(raw) ? raw : [];
};

const headScopeDepartmentNameFromTracking = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
): string => {
  let deptName = '';
  trackingItemsOnLog(log).forEach((item) => {
    if (String(item?.trackingItem || '') !== 'track_head_scope') return;
    const kind = String(item?.recordKind || '').trim().toLowerCase();
    if (kind !== 'department' && kind !== 'team') return;
    const raw = String(item?.value || '').trim();
    const parentIdx = raw.indexOf('|parent:');
    const name = (parentIdx === -1 ? raw : raw.slice(0, parentIdx)).trim();
    if (name) deptName = name;
  });
  return deptName;
};

const departmentScopeIdFromName = (
  deptName: string,
  scopeNameToId: Map<string, string>,
  byId: Map<string, ActivityGroup>,
): string | null => {
  const scopeId = scopeNameToId.get(String(deptName || '').trim().toLowerCase());
  if (!scopeId) return null;
  const group = byId.get(scopeId);
  if (!group || group.kind !== 'department') return null;
  if (group.name.toLowerCase() === DEPARTMENTS_ROOT_SCOPE_NAME.toLowerCase()) return null;
  return scopeId;
};

const buildErpRecordById = (
  erpRecords: ErpRecordPartitionRow[],
): Map<string, ErpRecordPartitionRow> => {
  const erpById = new Map<string, ErpRecordPartitionRow>();
  erpRecords.forEach((row) => {
    if (row.id) erpById.set(row.id, row);
  });
  return erpById;
};

const buildErpHeadScopeToDeptScopeId = (
  erpRecords: ErpRecordPartitionRow[],
  scopeNameToId: Map<string, string>,
  groupById: Map<string, ActivityGroup>,
): Map<string, string> => {
  const byHeadScopeId = new Map<string, string>();
  const erpById = buildErpRecordById(erpRecords);
  const walkToDepartmentScope = (startId: string): string | null => {
    let id = startId;
    const seen = new Set<string>();
    for (let i = 0; i < 10; i += 1) {
      if (!id || seen.has(id)) return null;
      seen.add(id);
      const row = erpById.get(id);
      if (!row) return null;
      if (row.kind === 'department') {
        return departmentScopeIdFromName(row.name, scopeNameToId, groupById);
      }
      id = row.parentId;
    }
    return null;
  };
  erpById.forEach((_row, erpId) => {
    const deptScopeId = walkToDepartmentScope(erpId);
    if (deptScopeId) byHeadScopeId.set(erpId, deptScopeId);
  });
  // Location head_scope (e.g. Nigeria) — map to sole child department (e.g. Engineering).
  erpById.forEach((row, erpId) => {
    if (row.kind !== 'location') return;
    const childDepts: Array<{ name: string }> = [];
    erpById.forEach((child) => {
      if (child.kind === 'department' && child.parentId === erpId) {
        childDepts.push(child);
      }
    });
    if (childDepts.length !== 1) return;
    const deptScopeId = departmentScopeIdFromName(childDepts[0].name, scopeNameToId, groupById);
    if (deptScopeId) byHeadScopeId.set(erpId, deptScopeId);
  });
  return byHeadScopeId;
};

const resolveDepartmentFromActionDataParent = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
  erpById: Map<string, ErpRecordPartitionRow>,
  scopeNameToId: Map<string, string>,
  byId: Map<string, ActivityGroup>,
): string | null => {
  const actionDataId = logActionDataId(log);
  if (!actionDataId) return null;
  let id = actionDataId;
  const seen = new Set<string>();
  for (let i = 0; i < 12; i += 1) {
    if (!id || seen.has(id)) return null;
    seen.add(id);
    const row = erpById.get(id);
    if (!row) return null;
    if (row.kind === 'department') {
      return departmentScopeIdFromName(row.name, scopeNameToId, byId);
    }
    id = row.parentId;
  }
  return null;
};

const soleDepartmentPartitionScopeId = (
  byId: Map<string, ActivityGroup>,
): string | null => {
  const ids: string[] = [];
  byId.forEach((group, id) => {
    if (group.kind !== 'department') return;
    if (group.name.toLowerCase() === DEPARTMENTS_ROOT_SCOPE_NAME.toLowerCase()) return;
    ids.push(id);
  });
  return ids.length === 1 ? ids[0] : null;
};

const LOG_HEAD_SCOPE_NONE = '__no_head_scope__';

const logHeadScopeId = (log: {
  head_scope?: string | null,
  headScope?: string | null,
}): string => {
  const id = String(log.head_scope || log.headScope || '').trim();
  return id || LOG_HEAD_SCOPE_NONE;
};

const resolveDepartmentScopeForHeadScopeBucket = (
  headScopeId: string,
  erpHeadScopeToDeptScopeId: Map<string, string>,
): string | null => {
  if (!headScopeId || headScopeId === LOG_HEAD_SCOPE_NONE) return null;
  return erpHeadScopeToDeptScopeId.get(headScopeId) || null;
};

const resolveDepartmentScopeWithoutHeadScope = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
  scopeNameToId: Map<string, string>,
  byId: Map<string, ActivityGroup>,
  soleDeptScopeId: string | null,
): string | null => {
  const deptName = logDepartmentName(log)
    || headScopeDepartmentNameFromTracking(log);
  if (deptName) {
    const fromName = departmentScopeIdFromName(deptName, scopeNameToId, byId);
    if (fromName) return fromName;
  }
  if (soleDeptScopeId) return soleDeptScopeId;
  return null;
};

const addLogToDepartmentPartition = (
  log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
  part: {
    masterDataById: Map<string, ActivitySkuRef>,
    skuVoById: Map<string, ActivitySkuRef>,
    boActionsById: Map<string, ActivitySkuRef>,
    boActionLogCounts: Map<string, number>,
  },
  deptScopeId: string,
  masterDataIdSet: Set<string>,
  boActionLeafIdSet: Set<string>,
  masterDataDeptScopeId: Map<string, string>,
  boActionDeptScopeId: Map<string, string>,
  erpById: Map<string, ErpRecordPartitionRow>,
) => {
  const actionDataId = logActionDataId(log);
  const isVoMoney = logIsVoMoneyLog(log);

  // VO — money logs; checkbox id from action_data_id (or fallback).
  if (isVoMoney) {
    const voId = resolveVoCheckboxId(log);
    if (!voId) return;
    masterDataIdSet.add(voId);
    masterDataDeptScopeId.set(voId, deptScopeId);
    part.masterDataById.set(voId, {
      id: voId,
      name: resolveVoCheckboxLabel(log, voId, erpById),
    });
    collectSkuVoRefsFromLog(log, part.skuVoById);
    return;
  }

  // Money value on a non-VO row — not a BO partition leaf either.
  if (logHasMoneyTransactionValue(log)) return;
  const boLabel = logBoPartitionEventType(log);
  if (!boLabel || BO_PARTITION_EXCLUDED_EVENT_TYPES.has(boLabel)) return;
  const leafId = boActionLeafId(boLabel);
  boActionLeafIdSet.add(leafId);
  boActionDeptScopeId.set(leafId, deptScopeId);
  part.boActionsById.set(leafId, { id: leafId, name: boLabel });
  part.boActionLogCounts.set(leafId, (part.boActionLogCounts.get(leafId) || 0) + 1);
};

/** Build department VO/BO partition buckets from loaded activity logs. */
const buildDepartmentLogPartitions = (
  logs: BuildActivityGroupsModelOptions['logs'],
  scopeNameToId: Map<string, string>,
  byId: Map<string, ActivityGroup>,
  headScopeErpRecords: BuildActivityGroupsModelOptions['erpRecords'],
  erpRecords: BuildActivityGroupsModelOptions['erpRecords'],
): {
  partitions: Map<string, DepartmentLogPartition>,
  headScopeToDeptScopeId: Map<string, string>,
  masterDataIdSet: Set<string>,
  boActionLeafIdSet: Set<string>,
  masterDataDeptScopeId: Map<string, string>,
  boActionDeptScopeId: Map<string, string>,
} => {
  const mergedErpRecords = mergeErpRecordsForPartitions(headScopeErpRecords, erpRecords);
  const erpById = buildErpRecordById(mergedErpRecords);
  const erpHeadScopeToDeptScopeId = buildErpHeadScopeToDeptScopeId(
    mergedErpRecords,
    scopeNameToId,
    byId,
  );
  const soleDeptScopeId = soleDepartmentPartitionScopeId(byId);
  const partitions = new Map<string, {
    masterDataById: Map<string, ActivitySkuRef>,
    skuVoById: Map<string, ActivitySkuRef>,
    boActionsById: Map<string, ActivitySkuRef>,
    boActionLogCounts: Map<string, number>,
  }>();
  const headScopeToDeptScopeId = new Map<string, string>();
  const masterDataIdSet = new Set<string>();
  const boActionLeafIdSet = new Set<string>();
  const masterDataDeptScopeId = new Map<string, string>();
  const boActionDeptScopeId = new Map<string, string>();

  const ensurePartition = (deptScopeId: string) => {
    if (!partitions.has(deptScopeId)) {
      partitions.set(deptScopeId, {
        masterDataById: new Map(),
        skuVoById: new Map(),
        boActionsById: new Map(),
        boActionLogCounts: new Map(),
      });
    }
    return partitions.get(deptScopeId)!;
  };

  const assignLogToDepartment = (
    log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
    deptScopeId: string,
  ) => {
    const part = ensurePartition(deptScopeId);
    addLogToDepartmentPartition(
      log,
      part,
      deptScopeId,
      masterDataIdSet,
      boActionLeafIdSet,
      masterDataDeptScopeId,
      boActionDeptScopeId,
      erpById,
    );
  };

  const resolveDepartmentForLogInBucket = (
    log: NonNullable<BuildActivityGroupsModelOptions['logs']>[number],
  ): string | null => {
    const fromName = resolveDepartmentScopeWithoutHeadScope(
      log,
      scopeNameToId,
      byId,
      soleDeptScopeId,
    );
    if (fromName) return fromName;
    return resolveDepartmentFromActionDataParent(log, erpById, scopeNameToId, byId);
  };

  // 1) Share logs by head_scope id first — one department mapping per bucket when unambiguous.
  const logsByHeadScope = new Map<string, NonNullable<BuildActivityGroupsModelOptions['logs']>>();
  (logs || []).forEach((log) => {
    const headScopeId = logHeadScopeId(log);
    const list = logsByHeadScope.get(headScopeId) || [];
    list.push(log);
    logsByHeadScope.set(headScopeId, list);
  });

  logsByHeadScope.forEach((bucketLogs, headScopeId) => {
    if (headScopeId === LOG_HEAD_SCOPE_NONE) return;

    const bucketDeptScopeId = resolveDepartmentScopeForHeadScopeBucket(
      headScopeId,
      erpHeadScopeToDeptScopeId,
    );
    if (bucketDeptScopeId) {
      headScopeToDeptScopeId.set(headScopeId, bucketDeptScopeId);
      bucketLogs.forEach((log) => assignLogToDepartment(log, bucketDeptScopeId));
      return;
    }

    // Location / org head_scope with multiple departments — resolve per log.
    bucketLogs.forEach((log) => {
      const deptScopeId = resolveDepartmentForLogInBucket(log);
      if (!deptScopeId) return;
      assignLogToDepartment(log, deptScopeId);
    });
  });

  // 2) Legacy rows with no head_scope — resolve department per log (name / sole dept).
  (logsByHeadScope.get(LOG_HEAD_SCOPE_NONE) || []).forEach((log) => {
    const deptScopeId = resolveDepartmentScopeWithoutHeadScope(
      log,
      scopeNameToId,
      byId,
      soleDeptScopeId,
    );
    if (!deptScopeId) return;
    assignLogToDepartment(log, deptScopeId);
  });

  const out = new Map<string, DepartmentLogPartition>();
  partitions.forEach((part, deptScopeId) => {
    enrichPartitionSkuVoFromMasterData(part, mergedErpRecords);
    out.set(deptScopeId, {
      masterData: Array.from(part.masterDataById.values()).sort((a, b) => a.name.localeCompare(b.name)),
      skuVoItems: Array.from(part.skuVoById.values()).sort((a, b) => a.name.localeCompare(b.name)),
      boActions: Array.from(part.boActionsById.values())
        .filter((row) => (part.boActionLogCounts.get(row.id) || 0) > 0)
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
  });
  const mergedHeadScopeToDeptScopeId = new Map<string, string>([
    ...erpHeadScopeToDeptScopeId.entries(),
    ...headScopeToDeptScopeId.entries(),
  ]);
  return {
    partitions: out,
    headScopeToDeptScopeId: mergedHeadScopeToDeptScopeId,
    masterDataIdSet,
    boActionLeafIdSet,
    masterDataDeptScopeId,
    boActionDeptScopeId,
  };
};

/** Server partitionLogs + client logs — dedupe by id; server row wins. */
const mergePartitionLogSources = (
  partitionLogs?: BuildActivityGroupsModelOptions['logs'],
  logs?: BuildActivityGroupsModelOptions['logs'],
): BuildActivityGroupsModelOptions['logs'] => {
  const byId = new Map<string, NonNullable<BuildActivityGroupsModelOptions['logs']>[number]>();
  (partitionLogs || []).forEach((log) => {
    const id = String((log as { id?: string | null }).id || '').trim();
    if (id) byId.set(id, log);
  });
  (logs || []).forEach((log) => {
    const id = String((log as { id?: string | null }).id || '').trim();
    if (!id || byId.has(id)) return;
    byId.set(id, log);
  });
  return [...byId.values()];
};

const uniqueIds = (ids: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  ids.forEach((id) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
};

const skusForIds = (
  ids: string[],
  skuById: Map<string, ActivitySkuRef>,
): ActivitySkuRef[] => (
  ids
    .map((id) => skuById.get(id) || { id, name: id, hasValue: false })
);

const buildChildrenByParent = (
  groups: ActivityGroup[],
  links?: ActivityGroupLink[] | null,
): { childrenByParent: Map<string, string[]>, childIdSet: Set<string> } => {
  const groupById = new Map(groups.map((g) => [g.id, g]));
  const childrenByParent = new Map<string, string[]>();
  const childIdSet = new Set<string>();
  (links || []).forEach((link) => {
    const parentId = String(link?.parentId || '').trim();
    const childId = String(link?.childId || '').trim();
    if (!parentId || !childId || parentId === childId) return;
    if (!groupById.has(parentId) || !groupById.has(childId)) return;
    childIdSet.add(childId);
    const list = childrenByParent.get(parentId) || [];
    if (!list.includes(childId)) list.push(childId);
    childrenByParent.set(parentId, list);
  });
  return { childrenByParent, childIdSet };
};

/** Ids the tree checkbox toggles (SKUs + activity scopes under this node). */
export const skuNodeSelectionIds = (node: {
  skuIds?: string[],
  groupTypeIds?: string[],
}): string[] => uniqueIds([
  ...(node.skuIds || []),
  ...(node.groupTypeIds || []),
]);

/** Scopes-tab checkbox: activity-scope ids only (never SKUs or VO/BO). */
export const scopeNodeSelectionIds = (node: {
  groupTypeIds?: string[],
}): string[] => uniqueIds(node.groupTypeIds || []);

/** True when this scope lists SKUs (blue leaf on Scopes tab) rather than only child scopes. */
export const scopeNodeHasSkusNotOnlyChildren = (node: ActivitySkuHierarchyGroup): boolean => {
  if (node.isObjectGroup) return false;
  const hasDeptPartition = (node.children || []).some(
    (c) => c.isDepartmentPartitionGroup || c.isDepartmentBoAggregate,
  );
  if (hasDeptPartition) return false;
  const scopeChildren = (node.children || []).filter((c) => !c.isObjectGroup);
  return (node.skuIds || []).length > 0 && scopeChildren.length === 0;
};

const departmentPartitionObjectChildren = (
  node: ActivitySkuHierarchyGroup,
): ActivitySkuHierarchyGroup[] => (
  (node.children || []).filter(
    (c) => c.isObjectGroup && (c.isDepartmentPartitionGroup || c.isDepartmentBoAggregate),
  )
);

/**
 * Scopes-tab tokens for this node under this parent only.
 * A shared child under A is A>child — not the same key as B>child.
 */
export const collectScopeOccurrenceKeys = (
  node: ActivitySkuHierarchyGroup,
  parentPath: string[],
): string[] => {
  if (node.isObjectGroup) {
    const path = [...parentPath, node.id];
    // Groups tab — department VO master-data rows.
    if (node.isDepartmentPartitionGroup) {
      return (node.groupsVoRefs || []).map(
        (ref) => activityOccurrenceKey(path, ref.id),
      );
    }
    // Groups tab — one BO checkbox per unique event_type under this header.
    if (node.isDepartmentBoAggregate) {
      return (node.boActionRefs || []).map(
        (ref) => activityOccurrenceKey(path, ref.id),
      );
    }
    return [];
  }
  const path = [...parentPath, node.id];
  const partitionChildren = departmentPartitionObjectChildren(node);
  const scopeChildren = (node.children || []).filter((c) => !c.isObjectGroup);
  if (partitionChildren.length) {
    const partitionKeys = partitionChildren.flatMap(
      (c) => collectScopeOccurrenceKeys(c, path),
    );
    const nestedScopeKeys = scopeChildren.flatMap(
      (c) => collectScopeOccurrenceKeys(c, path),
    );
    return uniqueIds([...partitionKeys, ...nestedScopeKeys]);
  }
  if (!scopeChildren.length || scopeNodeHasSkusNotOnlyChildren(node)) {
    return [activityOccurrenceKey(parentPath, node.id)];
  }
  return uniqueIds(scopeChildren.flatMap((c) => collectScopeOccurrenceKeys(c, path)));
};

/** SKU-tab tokens: this occurrence’s group types + SKUs (not other parents’ copies). */
export const collectSkuOccurrenceKeys = (
  node: ActivitySkuHierarchyGroup,
  parentPath: string[],
): string[] => {
  const path = [...parentPath, node.id];
  if (node.isObjectGroup) {
    if (node.isDepartmentBoAggregate) {
      return (node.boActionRefs || []).map(
        (ref) => activityOccurrenceKey(path, ref.id),
      );
    }
    return uniqueIds((node.skus || []).map((s) => activityOccurrenceKey(path, s.id)));
  }
  const scopeChildren = (node.children || []).filter((c) => !c.isObjectGroup);
  const objectChildren = (node.children || []).filter((c) => c.isObjectGroup);
  const partitionChildren = departmentPartitionObjectChildren(node);
  if (partitionChildren.length) {
    const partitionKeys = partitionChildren.flatMap(
      (c) => collectSkuOccurrenceKeys(c, path),
    );
    const nestedScopeKeys = scopeChildren.flatMap(
      (c) => collectSkuOccurrenceKeys(c, path),
    );
    return uniqueIds([...partitionKeys, ...nestedScopeKeys]);
  }
  // Leaf with VO and/or BO: parent is fully on iff every wrapper that exists is
  // fully on. Missing VO or missing BO is not a leftover unticked child.
  if (objectChildren.length && !scopeChildren.length) {
    return uniqueIds(objectChildren.flatMap((c) => collectSkuOccurrenceKeys(c, path)));
  }
  const selfKeys = (!scopeChildren.length || scopeNodeHasSkusNotOnlyChildren(node)
    || (node.groupTypeIds || []).includes(node.id))
    ? [activityOccurrenceKey(parentPath, node.id)]
    : [];
  const skuKeys = (node.skus || []).map((s) => activityOccurrenceKey(path, s.id));
  const childKeys = (node.children || []).flatMap((c) => collectSkuOccurrenceKeys(c, path));
  return uniqueIds([...selfKeys, ...skuKeys, ...childKeys]);
};

export const collectAllActivityOccurrenceKeys = (model: ActivityGroupsModel): string[] => (
  uniqueIds((model.skuHierarchy || []).flatMap((root) => collectSkuOccurrenceKeys(root, [])))
);

/** Scopes-tab Show-all expand — never writes SKU keys into the scopes selection. */
export const collectAllScopeOccurrenceKeys = (model: ActivityGroupsModel): string[] => (
  uniqueIds((model.skuHierarchy || []).flatMap((root) => collectScopeOccurrenceKeys(root, [])))
);

/** Legacy flat id → every occurrence of that id in the tree. */
export const expandLegacyCheckedToOccurrenceKeys = (
  checked: string[],
  allKeys: string[],
): string[] => {
  const tokens = (checked || []).filter((c) => c && c !== SHOW_ALL_ACTIVITY_LOGS);
  const occurrenceSet = new Set(tokens.filter(isActivityOccurrenceKey));
  const flats = tokens.filter((c) => !isActivityOccurrenceKey(c));
  const byLeaf = new Map<string, string[]>();
  (allKeys || []).forEach((k) => {
    const leaf = selectionIdFromCheckedToken(k);
    const list = byLeaf.get(leaf) || [];
    list.push(k);
    byLeaf.set(leaf, list);
  });
  const out = [...occurrenceSet];
  flats.forEach((id) => {
    const occ = byLeaf.get(id);
    if (occ?.length) occ.forEach((k) => out.push(k));
    else out.push(id);
  });
  return uniqueIds(out);
};

export const occurrenceKeysCheckState = (
  keys: string[],
  checked: string[],
  allKeys: string[],
): 'off' | 'partial' | 'on' => {
  if (checkedIncludesShowAll(checked)) return 'on';
  const ids = keys || [];
  if (!ids.length) return 'off';
  const expanded = new Set(expandLegacyCheckedToOccurrenceKeys(checked, allKeys));
  const count = ids.filter((k) => expanded.has(k)).length;
  if (count === 0) return 'off';
  if (count === ids.length) return 'on';
  return 'partial';
};

export const occurrenceKeyIsOn = (
  key: string,
  checked: string[],
  allKeys: string[],
): boolean => occurrenceKeysCheckState([key], checked, allKeys) === 'on';

/**
 * Unique leaf activity-scope names that are on or partial, in left-pane tree order.
 * Parents that only nest other scopes are skipped so the recap lists real scopes.
 */
export const collectActiveScopeNames = (
  roots: ActivitySkuHierarchyGroup[],
  checked: string[],
  allOccurrenceKeys: string[],
  forScopesTab: boolean,
): string[] => {
  const names: string[] = [];
  const seen = new Set<string>();
  const walk = (nodes: ActivitySkuHierarchyGroup[], parentPath: string[]) => {
    (nodes || []).forEach((node) => {
      if (node.isObjectGroup) return;
      const scopeChildren = (node.children || []).filter((c) => !c.isObjectGroup);
      const isLeaf = !scopeChildren.length || scopeNodeHasSkusNotOnlyChildren(node);
      if (!isLeaf) {
        walk(scopeChildren, [...parentPath, node.id]);
        return;
      }
      const keys = forScopesTab
        ? collectScopeOccurrenceKeys(node, parentPath)
        : collectSkuOccurrenceKeys(node, parentPath);
      if (occurrenceKeysCheckState(keys, checked, allOccurrenceKeys) === 'off') {
        return;
      }
      const label = String(node.name || '').trim();
      const nameKey = label.toLowerCase();
      if (!label || seen.has(nameKey)) return;
      seen.add(nameKey);
      names.push(label);
    });
  };
  walk(roots, []);
  return names;
};

const objectGroupNode = (
  skuType: ActivitySkuType,
  scopeId: string,
  scopeName: string,
  ids: string[],
  skuById: Map<string, ActivitySkuRef>,
): ActivitySkuHierarchyGroup => {
  const isVo = skuType === 'VO';
  return {
    id: `${isVo ? SKU_TOP_VO_ID : SKU_TOP_ACTIONS_ID}__${scopeId}`,
    name: skuObjectGroupLabel(scopeName, skuType),
    skuIds: ids,
    // Object groups only toggle SKUs — scope id stays on the parent scope node.
    groupTypeIds: [],
    skus: skusForIds(ids, skuById),
    children: [],
    isObjectGroup: true,
  };
};

const departmentPartitionNodes = (
  deptScopeId: string,
  partition: DepartmentLogPartition,
): ActivitySkuHierarchyGroup[] => {
  const voIds = partition.skuVoItems.map((s) => s.id);
  const boIds = partition.boActions.map((a) => a.id);
  const nodes: ActivitySkuHierarchyGroup[] = [];
  nodes.push({
    id: `${SKU_TOP_VO_ID}__${deptScopeId}`,
    name: DEPARTMENT_VO_HEADER,
    skuIds: voIds,
    groupTypeIds: [],
    groupsVoRefs: partition.masterData,
    skus: partition.skuVoItems,
    children: [],
    isObjectGroup: true,
    isDepartmentPartitionGroup: true,
  });
  if (partition.boActions.length) {
    nodes.push({
      id: `${SKU_TOP_ACTIONS_ID}__${deptScopeId}`,
      name: DEPARTMENT_BO_HEADER,
      skuIds: boIds,
      groupTypeIds: [],
      groupsVoRefs: [],
      skus: [],
      boActionRefs: partition.boActions,
      children: [],
      isObjectGroup: true,
      isDepartmentBoAggregate: true,
    });
  }
  return nodes;
};

const buildSkuHierarchy = (
  groups: ActivityGroup[],
  skus: ActivitySkuRef[],
  links?: ActivityGroupLink[] | null,
  departmentPartitions?: Map<string, DepartmentLogPartition> | null,
  departmentPartitionScopeIds?: Set<string> | null,
): ActivitySkuHierarchyGroup[] => {
  const skuById = new Map(skus.map((s) => [s.id, s]));
  const groupById = new Map(groups.map((g) => [g.id, g]));
  const { childrenByParent, childIdSet } = buildChildrenByParent(groups, links);

  // Leaf activity scope: wrap SKUs under VO (has value) or BO (value omitted).
  const groupTypeNode = (
    gt: ActivityGroupType,
  ): ActivitySkuHierarchyGroup => {
    const ids = uniqueIds(gt.skuIds || []);
    const voIds = ids.filter((id) => Boolean(skuById.get(id)?.hasValue));
    const boIds = ids.filter((id) => !Boolean(skuById.get(id)?.hasValue));
    const objectChild: ActivitySkuHierarchyGroup[] = [];
    if (voIds.length) {
      objectChild.push(objectGroupNode('VO', gt.id, gt.name, voIds, skuById));
    }
    if (boIds.length) {
      objectChild.push(objectGroupNode('Actions', gt.id, gt.name, boIds, skuById));
    }
    return {
      id: gt.id,
      name: gt.name,
      skuIds: ids,
      groupTypeIds: [gt.id],
      skus: [],
      children: objectChild,
    };
  };

  const groupNode = (
    group: ActivityGroup,
    ancestors: Set<string>,
  ): ActivitySkuHierarchyGroup => {
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(group.id);
    const linkedChildren: ActivitySkuHierarchyGroup[] = [];
    (childrenByParent.get(group.id) || []).forEach((childId) => {
      if (nextAncestors.has(childId)) return;
      const child = groupById.get(childId);
      if (!child) return;
      // /logs org row: Location → Department → Management only (tracker / record ids).
      if (group.kind === 'organization' && !String(child.id).startsWith('trk_')) return;
      linkedChildren.push(groupNode(child, nextAncestors));
    });
    const typeChildren = (group.groupTypes || []).map((gt) => groupTypeNode(gt));
    let children = [...typeChildren, ...linkedChildren];
    if (
      group.kind === 'department'
      && group.name.toLowerCase() !== DEPARTMENTS_ROOT_SCOPE_NAME.toLowerCase()
    ) {
      const partition = departmentPartitions?.get(group.id) || {
        masterData: [],
        skuVoItems: [],
        boActions: [],
      };
      const partitionNodes = departmentPartitionNodes(group.id, partition);
      children = [...partitionNodes, ...children];
      departmentPartitionScopeIds?.add(group.id);
    }
    const skuIds = uniqueIds(children.flatMap((c) => c.skuIds));
    const groupTypeIds = uniqueIds(children.flatMap((c) => c.groupTypeIds));
    return {
      id: group.id,
      name: group.name,
      skuIds,
      groupTypeIds,
      skus: [],
      children,
    };
  };

  const roots: ActivitySkuHierarchyGroup[] = [];
  groups.forEach((group) => {
    if (childIdSet.has(group.id)) return;
    const row = groupNode(group, new Set());
    if (
      !row.children.length
      && !row.skuIds.length
      && group.kind !== 'organization'
      && group.kind !== 'property'
      && group.kind !== 'department'
      && !String(group.id).startsWith('trk_')
    ) {
      return;
    }
    roots.push(row);
  });

  // Orphan SKUs used to sit under an "Ungrouped" VO/BO wrapper (SKU tab).
  // Scopes pane hides those wrappers, so the parent showed as an empty checkbox — skip it.

  return roots;
};

export const buildActivityGroupsModel = (
  groupsIn: Array<Partial<ActivityGroup> & {
    id?: string,
    groupTypes?: unknown,
    group_types?: unknown,
    linkedSkus?: unknown,
    linked_skus?: unknown,
    skuAssociations?: unknown,
    sku_associations?: unknown,
  }> | null | undefined,
  options?: BuildActivityGroupsModelOptions | null,
): ActivityGroupsModel => {
  if (!groupsIn?.length) return emptyModel();

  const byId = new Map<string, ActivityGroup>();
  const groupTypeById = new Map<string, ActivityGroupType>();
  const groupTypeNameToId = new Map<string, string>();
  const groups: ActivityGroup[] = [];

  groupsIn.forEach((g) => {
    if (!g?.id) return;
    const groupTypes = normalizeGroupTypes(
      g.groupTypes ?? g.group_types ?? g.linkedSkus ?? g.linked_skus
      ?? g.skuAssociations ?? g.sku_associations,
    );
    const row: ActivityGroup = {
      id: String(g.id),
      name: String(g.name || '').trim() || String(g.id),
      kind: normalizeKind(g.kind),
      groupTypes,
    };
    byId.set(row.id, row);
    groups.push(row);
    groupTypes.forEach((gt) => {
      if (!groupTypeById.has(gt.id)) groupTypeById.set(gt.id, gt);
      const key = gt.name.toLowerCase();
      if (key && !groupTypeNameToId.has(key)) groupTypeNameToId.set(key, gt.id);
    });
  });

  const groupTypeIds = Array.from(groupTypeById.keys());
  const groupTypeIdSet = new Set(groupTypeIds);
  const scopeNameToId = new Map<string, string>();
  groups.forEach((g) => {
    const key = g.name.toLowerCase();
    if (key && !scopeNameToId.has(key)) scopeNameToId.set(key, g.id);
  });
  const { childrenByParent } = buildChildrenByParent(groups, options?.links);

  const inventoryNames = new Map<string, string>();
  const inventoryHasValue = new Map<string, boolean>();
  (options?.inventory || []).forEach((item) => {
    const id = String(item?.id || '').trim();
    if (!id) return;
    const name = String(item?.name || '').trim();
    if (name) inventoryNames.set(id, name);
    inventoryHasValue.set(id, skuValueIsPresent(item?.price));
  });

  // Prefer unique SKUs from logs when loaded; fill gaps from groupTypes.skuIds.
  const fromLogs = uniqueSkusFromLogs(options?.logs);
  const skuMetaById = new Map<string, { name: string, hasValue: boolean }>();
  fromLogs.forEach((s) => {
    skuMetaById.set(s.id, { name: s.name, hasValue: Boolean(s.hasValue) });
  });

  groupTypeById.forEach((gt) => {
    (gt.skuIds || []).forEach((skuId) => {
      const prev = skuMetaById.get(skuId);
      const invName = inventoryNames.get(skuId);
      const invValued = Boolean(inventoryHasValue.get(skuId));
      if (!prev) {
        skuMetaById.set(skuId, {
          name: invName || skuId,
          hasValue: invValued,
        });
        return;
      }
      skuMetaById.set(skuId, {
        name: (prev.name === skuId && invName) ? invName : prev.name,
        hasValue: prev.hasValue || invValued,
      });
    });
  });

  const skus = Array.from(skuMetaById.entries())
    .map(([id, meta]) => ({ id, name: meta.name, hasValue: meta.hasValue }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const skuIds = skus.map((s) => s.id);

  const {
    partitions: departmentPartitions,
    headScopeToDeptScopeId,
    masterDataIdSet,
    boActionLeafIdSet,
    masterDataDeptScopeId,
    boActionDeptScopeId,
  } = buildDepartmentLogPartitions(
    mergePartitionLogSources(options?.partitionLogs, options?.logs),
    scopeNameToId,
    byId,
    options?.headScopeErpRecords,
    options?.erpRecords,
  );
  const departmentPartitionScopeIds = new Set<string>();
  const skuHierarchy = buildSkuHierarchy(
    groups,
    skus,
    options?.links,
    departmentPartitions,
    departmentPartitionScopeIds,
  );

  const skuIdsByLeafId = new Map<string, Set<string>>();
  const collectLinkedSkuIds = (id: string, stack: Set<string>): Set<string> => {
    const cached = skuIdsByLeafId.get(id);
    if (cached && !stack.has(id)) return cached;
    if (stack.has(id)) return new Set();
    stack.add(id);
    const out = new Set<string>();
    const gt = groupTypeById.get(id);
    if (gt) (gt.skuIds || []).forEach((skuId) => out.add(skuId));
    const group = byId.get(id);
    if (group) {
      (group.groupTypes || []).forEach((childGt) => {
        (childGt.skuIds || []).forEach((skuId) => out.add(skuId));
        collectLinkedSkuIds(childGt.id, stack).forEach((skuId) => out.add(skuId));
      });
      (childrenByParent.get(id) || []).forEach((childId) => {
        collectLinkedSkuIds(childId, stack).forEach((skuId) => out.add(skuId));
      });
    }
    stack.delete(id);
    skuIdsByLeafId.set(id, out);
    return out;
  };
  groups.forEach((g) => collectLinkedSkuIds(g.id, new Set()));
  groupTypeById.forEach((_, id) => collectLinkedSkuIds(id, new Set()));

  return {
    groups,
    byId,
    groupTypeById,
    groupTypeNameToId,
    scopeNameToId,
    groupTypeIds,
    groupTypeIdSet,
    childrenByParentId: childrenByParent,
    skus,
    skuIds,
    skuIdSet: new Set(skuIds),
    skuHierarchy,
    skuIdsByLeafId,
    masterDataIdSet,
    boActionLeafIdSet,
    departmentPartitionScopeIds,
    headScopeToDeptScopeId,
    masterDataDeptScopeId,
    boActionDeptScopeId,
  };
};

/** Synthetic root for /logs Groups — parent of every top-level activity scope. */
export const buildAllActivityScopeRoot = (
  roots: ActivitySkuHierarchyGroup[],
): ActivitySkuHierarchyGroup | null => {
  if (!roots?.length) return null;
  return {
    id: ALL_ACTIVITY_SCOPE_ID,
    name: ALL_ACTIVITY_SCOPE_NAME,
    skuIds: uniqueIds(roots.flatMap((r) => r.skuIds || [])),
    groupTypeIds: uniqueIds(roots.flatMap((r) => r.groupTypeIds || [])),
    skus: [],
    children: roots,
  };
};

/** /logs left pane — organization activity scope (Blue Primus) at the root, then scopes. */
export const activityLogsScopeTreeRoots = (
  model: ActivityGroupsModel,
): ActivitySkuHierarchyGroup[] => model.skuHierarchy || [];

export const allActivityScopeOccurrenceKeys = (model: ActivityGroupsModel): string[] => {
  const root = buildAllActivityScopeRoot(model.skuHierarchy || []);
  if (!root) return [];
  return collectScopeOccurrenceKeys(root, []);
};

export const checkedIncludesShowAll = (checked: string[]): boolean => (
  (checked || []).includes(SHOW_ALL_ACTIVITY_LOGS)
  || (checked || []).includes(ALL_ACTIVITY_SCOPE_ID)
);

/** Drill path still valid after scope tree reload (e.g. after reseed). */
export const isScopeBranchKeyInHierarchy = (
  branchKey: string | null | undefined,
  roots: ActivitySkuHierarchyGroup[],
  keyPrefix = 'tree',
): boolean => {
  if (!branchKey) return true;
  const walk = (nodes: ActivitySkuHierarchyGroup[], prefix: string): boolean => {
    for (const node of nodes) {
      const nodeKey = `${prefix}:${node.id}`;
      if (nodeKey === branchKey) return true;
      if (branchKey.startsWith(`${nodeKey}:`)) {
        if (node.isObjectGroup) {
          if (walk([node], nodeKey)) return true;
          continue;
        }
        const scopeChildren = (node.children || []).filter((c) => !c.isObjectGroup);
        if (walk(scopeChildren, nodeKey)) return true;
        const partitionChildren = (node.children || []).filter(
          (c) => c.isDepartmentPartitionGroup || c.isDepartmentBoAggregate,
        );
        if (partitionChildren.length && walk(partitionChildren, nodeKey)) return true;
      }
    }
    return false;
  };
  return walk(roots || [], keyPrefix);
};

/** Resolve log.groupType || log.type name → groupType UUID. */
export const resolveLogGroupTypeId = (
  log: { groupType?: string | null, type?: string | null },
  model: ActivityGroupsModel,
): string | null => {
  const raw = String(log.groupType || log.type || '').trim();
  if (!raw) return null;
  if (model.groupTypeIdSet.has(raw)) return raw;
  const scopeByName = model.scopeNameToId.get(raw.toLowerCase());
  if (scopeByName) return scopeByName;
  return model.groupTypeNameToId.get(raw.toLowerCase()) || null;
};

/** Leaf activity-scope ids under this activity scope (own groupTypes + linked descendants). */
export const groupTypeIdsForGroup = (
  groupId: string,
  model: ActivityGroupsModel,
): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (id: string) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    const group = model.byId.get(id);
    if (!group) return;
    (group.groupTypes || []).forEach((gt) => {
      if (!gt.id || seen.has(gt.id)) return;
      seen.add(gt.id);
      out.push(gt.id);
    });
    (model.childrenByParentId.get(id) || []).forEach(walk);
  };
  walk(groupId);
  return out;
};

/** Toggle an activity-scope header: cascade all descendant leaf activity-scope ids. */
export const toggleGroupSelection = (
  groupId: string,
  checked: string[],
  model: ActivityGroupsModel,
): string[] => {
  const typeIds = groupTypeIdsForGroup(groupId, model);
  if (!typeIds.length) return (checked || []).filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS);

  const showAll = checkedIncludesShowAll(checked);
  const withoutShowAll = (checked || []).filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS);
  const allOn = showAll || typeIds.every((id) => withoutShowAll.includes(id));

  if (allOn) {
    const drop = new Set(typeIds);
    if (showAll) {
      const next = new Set([...model.groupTypeIds, ...model.skuIds]);
      drop.forEach((id) => next.delete(id));
      return Array.from(next);
    }
    return withoutShowAll.filter((c) => !drop.has(c));
  }

  const next = new Set(withoutShowAll);
  typeIds.forEach((id) => next.add(id));
  return Array.from(next);
};

export const toggleGroupTypeSelection = (
  groupTypeId: string,
  checked: string[],
  model: ActivityGroupsModel,
): string[] => {
  if (!model.groupTypeIdSet.has(groupTypeId)) return checked || [];
  const withoutShowAll = (checked || []).filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS);
  if (withoutShowAll.includes(groupTypeId)) {
    return withoutShowAll.filter((c) => c !== groupTypeId);
  }
  return [...withoutShowAll, groupTypeId];
};

export const toggleSkuSelection = (
  skuId: string,
  checked: string[],
  _model: ActivityGroupsModel,
): string[] => {
  const id = String(skuId || '').trim();
  if (!id) return checked || [];
  const withoutShowAll = (checked || []).filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS);
  if (withoutShowAll.includes(id)) {
    return withoutShowAll.filter((c) => c !== id);
  }
  return [...withoutShowAll, id];
};

/** Toggle this tree occurrence only — shared children under other parents stay as they are. */
export const toggleOccurrenceSelection = (
  occurrenceKeys: string[],
  checked: string[],
  model: ActivityGroupsModel,
  /** Per-tab expand set so Scopes Show-all never spills into SKU keys. */
  expandKeys?: string[],
): string[] => {
  const keys = uniqueIds((occurrenceKeys || []).filter(Boolean));
  if (!keys.length) return (checked || []).filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS);

  const allKeys = expandKeys || collectAllActivityOccurrenceKeys(model);
  const showAll = checkedIncludesShowAll(checked);
  const expanded = showAll
    ? allKeys
    : expandLegacyCheckedToOccurrenceKeys(checked, allKeys);
  const allOn = keys.every((k) => expanded.includes(k));

  if (allOn) {
    const drop = new Set(keys);
    return expanded.filter((k) => !drop.has(k));
  }

  const next = new Set(expanded);
  keys.forEach((k) => next.add(k));
  return Array.from(next);
};

/** Toggle every SKU / activity-scope id in a list (legacy global ids). */
export const toggleSkuIdsSelection = (
  skuIds: string[],
  checked: string[],
  model: ActivityGroupsModel,
): string[] => {
  const valid = (skuIds || []).filter(
    (id) => model.skuIdSet.has(id) || model.groupTypeIdSet.has(id) || isActivityOccurrenceKey(id),
  );
  if (!valid.length) return (checked || []).filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS);
  if (valid.some(isActivityOccurrenceKey)) {
    return toggleOccurrenceSelection(valid, checked, model);
  }

  const showAll = checkedIncludesShowAll(checked);
  const withoutShowAll = (checked || []).filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS);
  const allOn = showAll || valid.every((id) => withoutShowAll.includes(id));

  if (allOn) {
    const drop = new Set(valid);
    if (showAll) {
      const next = new Set([...model.groupTypeIds, ...model.skuIds]);
      drop.forEach((id) => next.delete(id));
      return Array.from(next);
    }
    return withoutShowAll.filter((c) => !drop.has(c));
  }

  const next = new Set(withoutShowAll);
  valid.forEach((id) => next.add(id));
  return Array.from(next);
};

export const selectionForShowAll = (): string[] => (
  [SHOW_ALL_ACTIVITY_LOGS]
);

export const groupCheckState = (
  groupId: string,
  checked: string[],
  model: ActivityGroupsModel,
): 'off' | 'partial' | 'on' => {
  if (checkedIncludesShowAll(checked)) return 'on';
  const typeIds = groupTypeIdsForGroup(groupId, model);
  if (!typeIds.length) return 'off';
  const count = typeIds.filter((id) => (checked || []).includes(id)).length;
  if (count === 0) return 'off';
  if (count === typeIds.length) return 'on';
  return 'partial';
};

/** Checkbox state for a set of SKU / scope ids (tree headers). */
export const skuIdsCheckState = (
  skuIds: string[],
  checked: string[],
): 'off' | 'partial' | 'on' => {
  if (checkedIncludesShowAll(checked)) return 'on';
  const ids = skuIds || [];
  if (!ids.length) return 'off';
  const count = ids.filter((id) => (checked || []).includes(id)).length;
  if (count === 0) return 'off';
  if (count === ids.length) return 'on';
  return 'partial';
};

/** True when the log names at least one SKU id (inventory / VO / BO line). */
export const logHasSkuSelectionIds = (
  log: { skus?: ActivityLogSkuRef[] | null },
): boolean => (
  (log.skus || []).some((s) => Boolean(String(s?.id || '').trim()))
);

/**
 * Visible iff Show all, or a checked occurrence matches the log's group type / SKU
 * and — when the path includes a branch scope — log.departments matches that branch.
 *
 * Groups tab + SKU tab: both picks apply together (AND). A group only keeps
 * SKUs linked to it. Groups all off + SKUs A,B → A and B. Then tick a group
 * linked to A only → A.
 * Empty SKU pane is not “all SKUs”: a log with a SKU stays hidden until that
 * SKU is ticked (or Show all). Logs with no SKU can still match Groups.
 */
export const logMatchesActivityGroupsSelection = (
  log: {
    groupType?: string | null,
    type?: string | null,
    department?: string | null,
    departments?: string | null,
    skus?: ActivityLogSkuRef[] | null,
    relatedGroups?: Array<{ id?: string | null } | string> | null,
  },
  checked: string[],
  model: ActivityGroupsModel,
  skuChecked?: string[] | null,
): boolean => {
  const scopesChecked = checked || [];
  const skuTokensIn = skuChecked == null ? null : (skuChecked || []);

  if (skuTokensIn != null) {
    const scopesEmpty = !scopesChecked.length;
    const skusEmpty = !skuTokensIn.length;
    const scopesShowAll = checkedIncludesShowAll(scopesChecked);
    const skusShowAll = checkedIncludesShowAll(skuTokensIn);
    if (scopesEmpty && skusEmpty) return false;
    const scopesOpen = scopesEmpty || scopesShowAll;
    // Empty SKUs ≠ unconstrained — unlike empty Groups (SKU-only still works).
    const skusOpen = skusShowAll;
    if (scopesOpen && skusOpen) return true;
    const scopeOk = scopesOpen
      || logMatchesActivityGroupsTokens(
        log,
        scopesChecked,
        model,
        skusOpen ? 'scope' : 'scopeLinked',
      );
    const skuOk = skusOpen
      || (skusEmpty
        ? !logHasSkuSelectionIds(log)
        : logMatchesActivityGroupsTokens(log, skuTokensIn, model, 'sku'));
    return scopeOk && skuOk;
  }

  if (!scopesChecked.length) return false;
  if (checkedIncludesShowAll(scopesChecked)) return true;
  return logMatchesActivityGroupsTokens(log, scopesChecked, model, 'any');
};

const logRelatedGroupIds = (
  log: { relatedGroups?: Array<{ id?: string | null } | string> | null },
): string[] => (
  (log.relatedGroups || []).map((g) => (
    typeof g === 'string' ? g : String(g?.id || '')
  )).map((id) => id.trim()).filter(Boolean)
);

/** Expand checked tokens once — callers filter many logs with logMatchesActivityGroupsOccurrenceKeys. */
export const expandActivityGroupsTokens = (
  tokensIn: string[],
  model: ActivityGroupsModel,
  mode: 'scope' | 'scopeLinked' | 'sku' | 'any',
): string[] => {
  const tokens = (tokensIn || []).filter((c) => c && c !== SHOW_ALL_ACTIVITY_LOGS);
  if (!tokens.length) return [];
  const allKeys = mode === 'sku'
    ? collectAllActivityOccurrenceKeys(model)
    : collectAllScopeOccurrenceKeys(model);
  return expandLegacyCheckedToOccurrenceKeys(
    tokens,
    mode === 'any' ? collectAllActivityOccurrenceKeys(model) : allKeys,
  );
};

/** Match using pre-expanded occurrence keys (no per-log tree walks). */
export const logMatchesActivityGroupsOccurrenceKeys = (
  log: {
    groupType?: string | null,
    type?: string | null,
    action?: string | null,
    action_data_id?: string | null,
    actionDataId?: string | null,
    head_scope?: string | null,
    headScope?: string | null,
    department?: string | null,
    departments?: string | null,
    skus?: ActivityLogSkuRef[] | null,
    relatedGroups?: Array<{ id?: string | null } | string> | null,
    tracking_items?: Array<{ trackingItem?: string, recordKind?: string, recordCode?: string, value?: string }> | null,
    trackingItems?: Array<{ trackingItem?: string, recordKind?: string, recordCode?: string, value?: string }> | null,
  },
  occurrenceKeys: string[],
  model: ActivityGroupsModel,
  mode: 'scope' | 'scopeLinked' | 'sku' | 'any',
): boolean => {
  if (!occurrenceKeys.length) return false;

  const groupTypeId = resolveLogGroupTypeId(log, model);
  const logSkuIds = (log.skus || [])
    .map((s) => String(s?.id || '').trim())
    .filter(Boolean);
  const relatedIds = logRelatedGroupIds(log);
  const logDept = logDepartmentName(log);
  const masterDataId = logActionDataId(log);
  const boLabel = logBoActionLabel(log);
  const logHeadScope = String(log.head_scope || log.headScope || '').trim();
  const isVoMoney = logIsVoMoneyLog(log);
  const voCheckboxId = resolveVoCheckboxId(log);

  const logSkuIdsFromTracking = (): string[] => {
    const ids = [...logSkuIds];
    trackingItemsOnLog(log).forEach((item) => {
      if (String(item?.trackingItem || '') !== 'track_sku') return;
      const id = String(item?.recordCode || '').trim();
      if (id) ids.push(id);
    });
    return uniqueIds(ids);
  };

  const logBelongsToDepartmentScope = (deptScopeId: string): boolean => {
    if (!deptScopeId) return false;
    const group = model.byId.get(deptScopeId);
    if (!group || group.kind !== 'department') return false;
    if (masterDataId && model.masterDataDeptScopeId.get(masterDataId) === deptScopeId) {
      return true;
    }
    if (
      voCheckboxId
      && model.masterDataDeptScopeId.get(voCheckboxId) === deptScopeId
    ) {
      return true;
    }
    if (
      boLabel
      && model.boActionDeptScopeId.get(boActionLeafId(boLabel)) === deptScopeId
    ) {
      return true;
    }
    if (logHeadScope) {
      return model.headScopeToDeptScopeId.get(logHeadScope) === deptScopeId;
    }
    const deptName = String(group.name || '').trim();
    if (deptName && logDept && logDept.toLowerCase() === deptName.toLowerCase()) return true;
    // Legacy rows with no head_scope and no departments column — sole-dept bucket.
    if (
      !logDept
      && model.departmentPartitionScopeIds.size === 1
      && model.departmentPartitionScopeIds.has(deptScopeId)
    ) {
      return true;
    }
    return false;
  };

  const departmentScopeIdFromPath = (pathIds: string[]): string | null => {
    for (let i = pathIds.length - 1; i >= 0; i -= 1) {
      const id = pathIds[i];
      if (model.departmentPartitionScopeIds.has(id)) return id;
      if (model.byId.get(id)?.kind === 'department') return id;
    }
    return null;
  };

  const leafMatchesLog = (leaf: string, pathIds: string[]): boolean => {
    const deptScopeId = departmentScopeIdFromPath(pathIds);

    if (model.masterDataIdSet.has(leaf)) {
      if (voCheckboxId !== leaf) return false;
      if (!isVoMoney) return false;
      return !deptScopeId || logBelongsToDepartmentScope(deptScopeId);
    }

    if (isBoActionLeafId(leaf)) {
      if (logIsVoMoneyLog(log)) return false;
      if (leaf !== boActionLeafId(boLabel)) return false;
      return !deptScopeId || logBelongsToDepartmentScope(deptScopeId);
    }

    if (leaf.startsWith(`${SKU_TOP_ACTIONS_ID}__`) && !logIsVoMoneyLog(log)) {
      const aggregateDeptId = leaf.slice(`${SKU_TOP_ACTIONS_ID}__`.length);
      if (deptScopeId && aggregateDeptId !== deptScopeId) return false;
      const deptId = deptScopeId || aggregateDeptId;
      return Boolean(deptId) && logBelongsToDepartmentScope(deptId);
    }

    // Money logs only match department VO checkboxes — not parent scope rows.
    if (isVoMoney) return false;

    if (mode === 'sku') {
      return logSkuIdsFromTracking().includes(leaf);
    }
    const linked = model.skuIdsByLeafId.get(leaf);
    const hasLinked = Boolean(linked && linked.size);
    const linkedHit = Boolean(hasLinked && logSkuIds.some((id) => linked!.has(id)));
    if (mode === 'scopeLinked') {
      if (hasLinked) return linkedHit;
      if (groupTypeId && leaf === groupTypeId) return true;
      if (relatedIds.includes(leaf)) return true;
      return false;
    }
    if (groupTypeId && leaf === groupTypeId) return true;
    if (relatedIds.includes(leaf)) return true;
    if (linkedHit) return true;
    if (mode === 'any' && logSkuIds.includes(leaf)) return true;
    return false;
  };

  for (let i = 0; i < occurrenceKeys.length; i += 1) {
    const key = occurrenceKeys[i];
    const parsed = parseActivityOccurrenceKey(key);
    if (parsed) {
      if (!leafMatchesLog(parsed.leaf, parsed.path)) continue;
      // VO/BO partition leaves — department rules already applied in leafMatchesLog.
      if (
        model.masterDataIdSet.has(parsed.leaf)
        || isBoActionLeafId(parsed.leaf)
        || parsed.leaf.startsWith(`${SKU_TOP_ACTIONS_ID}__`)
      ) {
        return true;
      }
      // Head-scoped BO/scope rows — VO money logs need an explicit VO checkbox.
      if (logHeadScope && !isVoMoney) return true;
      const branches = branchDepartmentNamesInPath(parsed.path, model);
      if (logMatchesBranchDepartments(logDept, branches)) return true;
      continue;
    }
    const flat = selectionIdFromCheckedToken(key);
    if (leafMatchesLog(flat, [])) return true;
  }
  return false;
};

const logMatchesActivityGroupsTokens = (
  log: {
    groupType?: string | null,
    type?: string | null,
    department?: string | null,
    departments?: string | null,
    skus?: ActivityLogSkuRef[] | null,
    relatedGroups?: Array<{ id?: string | null } | string> | null,
  },
  tokensIn: string[],
  model: ActivityGroupsModel,
  mode: 'scope' | 'scopeLinked' | 'sku' | 'any',
): boolean => {
  const occurrenceKeys = expandActivityGroupsTokens(tokensIn, model, mode);
  if (!occurrenceKeys.length) return false;
  return logMatchesActivityGroupsOccurrenceKeys(log, occurrenceKeys, model, mode);
};

/** Activity column: activity-scope string on the log. */
export const activityLogsTableGroupTypeName = (
  log: { groupType?: string | null, type?: string | null },
): string => (
  String(log.groupType || log.type || '').trim()
);

/** Reveal a log: check its activity-scope id + sku ids. */
export const activityLogsCheckedToRevealLogSelection = (
  checked: string[],
  log: {
    groupType?: string | null,
    type?: string | null,
    skus?: ActivityLogSkuRef[] | null,
  },
  model: ActivityGroupsModel,
): string[] => {
  const next = new Set((checked || []).filter((c) => c !== SHOW_ALL_ACTIVITY_LOGS));
  const gtId = resolveLogGroupTypeId(log, model);
  if (gtId) next.add(gtId);
  (log.skus || []).forEach((s) => {
    const id = String(s?.id || '').trim();
    if (id) next.add(id);
  });
  return Array.from(next);
};
